import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import moment from 'moment';
import styled from 'styled-components';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import LoggedLayout from 'layouts/LoggedLayout';
import PopupLayout from 'layouts/PopupLayout';
import {CristalLoader, loader} from 'helpers/generic';
import FieldSmallComment from 'components/FieldSmallComment';
import CustomInput from 'components/CustomInput';
import CustomSelect from 'components/CustomSelect';
import { getCommunity } from 'helpers/community';
import CounterPartsField  from './CounterPartsField';

const TermsWrapper = styled.div`
	position: relative;

	#overlay-data {
		position: absolute;
		top: 0;
		right: 0;
		height: 100%;
		z-index: 2;

		display: flex;
		align-items: center;
		font-size: 9px;
		padding-right: 5px;

		#loader {
			height: 100%;
			aspect-ratio: 1/1;
		}
	}
`;

let axiosCancelToken = null;

let parentWindowProxyCallback = window.opener?.BuildingPopupProxyCallback;

export default function BankReconciliationsRulesForm(props) {
	const navigate = useNavigate();
	const params = useParams();
	const [queryParams] = useSearchParams();

	const popup = queryParams.get('popup') === 'true';
	const concept = queryParams.get('concept');
	const year = queryParams.get('year');

	let [data, setData] = useState({
		type: 'counterpart',
		year: year ?? moment().year(),
		active: 1,
		data: {
			term: concept ?? '',
			counterpart_type: 'expense'
		}
	});
	let [errors, setErrors] = useState({});
	let [loading, setLoading] = useState(false);
	// let [counterPart, setCounterPart] = useState(null);
	let [loadingSearchingTermCoincidences, setLoadingSearchingTermCoincidences] = useState(false);
	let [searchTermCoincidencesCount, setSearchTermCoincidencesCount] = useState(0);
	let [yearOnBlur, setYearOnBlur] = useState(null);

	const searchCoincidencesTimeoutRef = useRef(null);

	useEffect(() => {
		axiosCancelToken = axios.CancelToken.source();
	
		return function cleanup() {
           	axiosCancelToken.cancel();
        }
	}, []);

	useEffect(() => {
		if ( params.id ) {
			const getData = async () => {
				setLoading(true);
				setData([]);

				await axios.get('/api/manager/bank-reconciliation-rules/get/' + params.id, {
					params: {
						community_id: getCommunity()?.id
					},
					cancelToken: axiosCancelToken.token
				}).then((response) => {
					const rule = response.data;

					if (rule.type === 'counterpart') {
						// setCounterPart(rule.data.counterpart ?? null);

						// Fix for older rules, now is mandatory this field if is counterpart
						if (!rule.data.counterpart_type) {
							rule.data.counterpart_type = 'expense';
						}
					}

					if (rule.data.term) {
						searchTermCoincidences(rule.data.term);
					}

			    	setData(rule);
			  	}).catch((error) => {
					if ( axios.isCancel(error) ) return;
				});

				setLoading(false);
			}
			getData();
		} else {
			if (concept) {
				searchTermCoincidences(concept);
			}
		}
	}, [params.id, concept]);

	const deleteRule = () => {
		const c = window.confirm('¿Quieres eliminar esta regla?');
		if ( !c ) return;

		axios.delete('/api/manager/bank-reconciliation-rules/delete/' + data.id + '?community_id=' + getCommunity()?.id, {}, {
			cancelToken: axiosCancelToken.token
		}).then((response) => {
	    	toast.info('Regla borrada')
	    	navigate('/bank-reconciliation/rules');
	  	}).catch((error) => {
			if ( axios.isCancel(error) ) return;
		});	
	}

	const saveData = (goBack = true) => {
		setErrors({});

		data.community_id = getCommunity()?.id;

		axios.post('/api/manager/bank-reconciliation-rules/' + (data.id ? 'edit/' + data.id : 'add'), data, {
			cancelToken: axiosCancelToken.token
		}).then((response) => {
			toast.success('Datos guardados');
			setData({...response.data.rule});

			if ( goBack ) {
				if ( popup ) {
					window.close();
					if (parentWindowProxyCallback) parentWindowProxyCallback(response.data.rule);
				}
				else navigate('/bank-reconciliation/rules');
			} else {
		    	navigate('/bank-reconciliation/rules/edit/' + response.data.rule.id + (popup ? '?popup=true' : ''));
			}
	  	}).catch((error) => {
			if ( axios.isCancel(error) ) return;
			if ( error.response.data.errors ) setErrors(error.response.data.errors);
			toast.error('Ha ocurrido un error al guardar');
		});	
	}

	const setDataField = (field, value) => {
		setData({...data, [field]: value});
	}

	const setDataColumnField = (field, value) => {
		setData({...data, data: {...data.data, [field]: value}});
	}

	const updateTerm = (value) => {
		setDataColumnField('term', value);
		searchTermCoincidences(value);
	}

	const searchTermCoincidences = (value) => {
		if (searchCoincidencesTimeoutRef.current) {
			clearTimeout(searchCoincidencesTimeoutRef.current);
		}

		setLoadingSearchingTermCoincidences(true);

		searchCoincidencesTimeoutRef.current = setTimeout(() => {
			axios.get('/api/manager/bank-reconciliation-rules/search-coincidences', {
				params: {
					term: value,
					year: data.year,
					community_id: getCommunity()?.id
				},
				cancelToken: axiosCancelToken.token
			}).then((response) => {
				setSearchTermCoincidencesCount(response.data.count);
			}).catch((error) => {
				if ( axios.isCancel(error) ) return;
			}).finally(() => {
				setLoadingSearchingTermCoincidences(false);
			});
		}, 500);
	}

	const handleYearOnBlur = (value) => {
		if (!yearOnBlur) {
			setYearOnBlur(value);
			return;
		}

		if (yearOnBlur && yearOnBlur !== value && data.data?.counterparts?.length > 0) {
			const c = window.confirm('¿Quieres cambiar el año fiscal? Se quitarán las contrapartidas y tendrás que volver a añadirlas');
			if (!c) {
				setData(prev => ({...prev, year: yearOnBlur}));
				return;
			}

			setData(prev => ({...prev, data: {...prev.data, counterparts: [], year: value}}));
		}
	}

	const Layout = popup ? PopupLayout : LoggedLayout;

	return (
		<Layout>
			{ loading && <CristalLoader /> }

			<section>
				<div className="page-title">
					<h1>Ficha de regla de conciliación automática</h1>
					<button onClick={() => popup ? window.close() : navigate('/bank-reconciliation/rules')} className="btn btn-sm btn-light ms-auto">{popup ? 'Cerrar' : 'Volver'}</button>
				</div>

				<div className="page-content">
					<div className="row justify-content-center">
						<div className="col-lg-8">
							<div className="card border-0 shadow-sm">
								<div className="card-body">
									<div className="row">
										<div className="col-lg-3">
											<div className="mb-2">
												<CustomSelect label="Tipo" className="form-control form-control-sm" onChange={(e) => setDataField('type', e.target.value)} value={data.type ?? ''}>
													<option value=""></option>
													<option value="counterpart">Contrapartida</option>
													<option value="bankremittance">Remesa</option>
												</CustomSelect>
												{ errors.type &&
													<div className="invalid-feedback d-block">{ errors.type[0] }</div>
												}
											</div>
										</div>

										<div className="col-lg-3">
											<div className="mb-2">
												<CustomInput 
													label="Año fiscal" 
													type="number" 
													className="form-control form-control-sm" 
													onChange={(e) => setDataField('year', e.target.value)} 
													onBlur={(e) => handleYearOnBlur(e.target.value)}
													value={data.year ?? ''} 
												/>
												{ errors.year &&
													<div className="invalid-feedback d-block">{ errors.year[0] }</div>
												}
											</div>
										</div>

										<div className="col-lg-3">
											<div className="mb-2">
												<CustomSelect label="Activa" className="form-control form-control-sm" onChange={(e) => setDataField('active', e.target.value)} value={data.active ?? ''}>
													<option value="0">No</option>
													<option value="1">Si</option>
												</CustomSelect>
												{ errors.active &&
													<div className="invalid-feedback d-block">{ errors.active[0] }</div>
												}
											</div>
										</div>

										{data.type === 'counterpart' &&
											<div className="col-lg-3">
												<div className="mb-2">
													<CustomSelect label="Tipo de contrapartida" className="form-control form-control-sm" onChange={(e) => setDataColumnField('counterpart_type', e.target.value)} value={data.data?.counterpart_type ?? ''}>
														<option value="expense">Gasto</option>
														<option value="income">Ingreso</option>
													</CustomSelect>
													{ errors['data.counterpart_type'] &&
														<div className="invalid-feedback d-block">{ errors['data.counterpart_type'][0] }</div>
													}
												</div>
											</div>
										}

										{(data.type === 'counterpart' || data.type === 'bankremittance') &&
											<div className={'col-lg-' + (data.type === 'counterpart' ? 7 : 12) + ' mt-2 mb-2'}>
												<TermsWrapper id="terms-wrapper">
													<div id="overlay-data">
														{loadingSearchingTermCoincidences && loader}
														{!loadingSearchingTermCoincidences && 
															<>
																{searchTermCoincidencesCount} coincidencias encontradas
															</>
														}
													</div>
													<CustomInput label="Término de búsqueda" type="text" className="form-control form-control-sm" onChange={(e) => updateTerm(e.target.value)} value={data.data?.term ?? ''} />
												</TermsWrapper>

												{ errors['data.term'] &&
													<div className="invalid-feedback d-block">{ errors['data.term'][0] }</div>
												}
											</div>
										}

										{data.type === 'counterpart' &&
											<>
												<div className="col-lg-5 mt-2 mb-2">
													<div className="row">
														<div className="col-md-6">
															<CustomInput label="Importe mínimo (opcional)" type="text" className="form-control form-control-sm" onChange={(e) => setDataColumnField('min_amount', e.target.value)} value={data.data?.min_amount ?? ''} />
															{ errors['data.min_amount'] &&
																<div className="invalid-feedback d-block">{ errors['data.min_amount'][0] }</div>
															}
														</div>
														<div className="col-md-6">
															<CustomInput label="Importe máximo (opcional)" type="text" className="form-control form-control-sm" onChange={(e) => setDataColumnField('max_amount', e.target.value)} value={data.data?.max_amount ?? ''} />
															{ errors['data.max_amount'] &&
																<div className="invalid-feedback d-block">{ errors['data.max_amount'][0] }</div>
															}
														</div>
													</div>
												</div>

												<div className="col-lg-12 mt-2 mb-2">
													<CounterPartsField 
														counterParts={data.data?.counterparts}
														setCounterParts={value => {
															setDataColumnField('counterparts', value)
														}}
														year={data.year}
														errors={errors}
														disabled={!data.year}
													/>

													{errors['data.counterparts'] &&
														<div className="invalid-feedback d-block">{ errors['data.counterparts'][0] }</div>
													}
												</div>
											</>
										}

										{data.type === 'bankremittance' &&
											<div className="col-lg-12 mt-2 mb-2">
												<FieldSmallComment>
													La regla tratará de buscar un movimiento bancario con el término 
													de búsqueda indicado y lo conciliará con una remesa bancaria
												</FieldSmallComment>
											</div>
										}

										{/* <div className="col-lg-3">
											<div className="mb-2">
												<CustomInput label="Orden" type="number" className="form-control form-control-sm" onChange={(e) => setDataField('order', e.target.value)} value={data.order ?? ''} />
												{ errors.order &&
													<div className="invalid-feedback d-block">{ errors.order[0] }</div>
												}
											</div>
										</div>
										<div className="col-lg-3">
											<div className="mb-2">
												<CustomSelect label="Por defecto" className="form-control form-control-sm" onChange={(e) => setDataField('default', e.target.value)} value={data.default ?? ''}>
													<option value="0">No</option>
													<option value="1">Si</option>
												</CustomSelect>
												<FieldSmallComment className="mt-1">Si se marca por defecto aparecerá como tipo preseleccionado en el formulario de avisos</FieldSmallComment>
												{ errors.default &&
													<div className="invalid-feedback d-block">{ errors.default[0] }</div>
												}
											</div>
										</div> */}
									</div>	
								</div>
								<div className="card-footer" id={popup ? 'footer-fixed' : ''}>
									<div className="row">
										<div className="col-6">
											{ data.id &&
												<button className="btn btn-sm btn-link text-danger" tabIndex="-1" onClick={() => deleteRule()}>Eliminar</button>							
											}
										</div>
										<div className="col-6 text-end">
											<button className="btn btn-sm btn-primary text-white d-inline ms-3" onClick={() => saveData(false)}>Guardar</button>							
											<button className="btn btn-sm btn-primary text-white d-inline ms-3" onClick={() => saveData()}>Guardar y salir</button>							
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>
		</Layout>
	);
}